import { Type } from '@sinclair/typebox';
/**
 * HUB-081 (WEB-02, WEB-05, WEB-07) — request/response schemas for the two
 * stateless web-research hub routes (`POST /web/search`, `POST /web/extract`).
 * Both are `trackId`-gated and budget-metered, mirroring `ai.ts`'s
 * `trackId: Type.String({ format: 'uuid' })` contract so every call is metered
 * against the same token budget. task-07's navigation routes extend this file.
 */
// POST /web/search — ranked web results. `trackId` is REQUIRED so every search
// is budget-gated and metered (token-equivalent cost, HUB-081).
export const WebSearchBodySchema = Type.Object({
    trackId: Type.String({ format: 'uuid' }),
    query: Type.String({ minLength: 1 }),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 50 })),
});
export const WebSearchResultSchema = Type.Object({
    title: Type.String(),
    url: Type.String(),
    snippet: Type.String(),
    /** Provider-native relevance rank, 0 = most relevant. */
    rank: Type.Integer({ minimum: 0 }),
});
export const WebSearchResponseSchema = Type.Object({
    results: Type.Array(WebSearchResultSchema),
});
// POST /web/extract — fetch a page, persist it through the task-05 pipeline, and
// return a LOCATOR (never the full body; the content reaches a model via recall,
// COP-101/COP-116). `refresh` is reserved for a forced re-fetch; V1 dedups by URL
// through `persistExtract`.
export const WebExtractBodySchema = Type.Object({
    trackId: Type.String({ format: 'uuid' }),
    url: Type.String({ minLength: 1 }),
    refresh: Type.Optional(Type.Boolean()),
});
export const WebExtractResponseSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    canonicalUrl: Type.String(),
    /** True when an existing same-(canonical URL, track) document was reused. */
    deduped: Type.Boolean(),
    title: Type.Optional(Type.String()),
});
// ---------------------------------------------------------------------------
// Live navigation (HUB-081, WEB-01/04/10; M26 task-07) — session-oriented routes
// over `tf_web_session`. `start` opens a session, `act` runs ONE navigation
// primitive against the persisted handle (reconnected fail-closed each call),
// `end` closes it. Every route is `trackId`-gated + budget-metered like the
// stateless pair above. `chatSessionId` participates in every call because the
// session store (task-04) is fail-closed on BOTH `user_id` AND `chat_session_id`
// — reconnect needs the chat-session scope to resolve the row.
// ---------------------------------------------------------------------------
/** The observable page state a navigation primitive lands on. */
export const NavPageStateSchema = Type.Object({
    url: Type.String(),
    title: Type.Optional(Type.String()),
    /** Current DOM text of the page. */
    text: Type.String(),
});
// POST /web/nav/start — open a live browse session bound to (user, chat session,
// track). Returns the persisted `tf_web_session.id` to reconnect on later turns.
export const WebNavStartBodySchema = Type.Object({
    trackId: Type.String({ format: 'uuid' }),
    chatSessionId: Type.String({ format: 'uuid' }),
});
export const WebNavStartResponseSchema = Type.Object({
    sessionId: Type.String({ format: 'uuid' }),
    page: NavPageStateSchema,
});
// POST /web/nav/act — run one primitive against the persisted handle. `navigate`
// needs `url`, `click` needs `selector`, `type` needs `selector` + `text`,
// `read` captures the current page through the task-05 pipeline. `chatSessionId`
// scopes the fail-closed reconnect (task-04).
export const WebNavActBodySchema = Type.Object({
    trackId: Type.String({ format: 'uuid' }),
    sessionId: Type.String({ format: 'uuid' }),
    chatSessionId: Type.String({ format: 'uuid' }),
    action: Type.Union([
        Type.Literal('navigate'),
        Type.Literal('click'),
        Type.Literal('type'),
        Type.Literal('read'),
    ]),
    url: Type.Optional(Type.String({ minLength: 1 })),
    selector: Type.Optional(Type.String({ minLength: 1 })),
    text: Type.Optional(Type.String()),
});
/** Locator for a page captured mid-navigation (a `read` action). */
export const WebNavCaptureSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    canonicalUrl: Type.String(),
});
export const WebNavActResponseSchema = Type.Object({
    page: NavPageStateSchema,
    capture: Type.Optional(WebNavCaptureSchema),
});
// POST /web/nav/end — close a live browse session. Fail-closed reconnect first,
// then the provider teardown; the row is marked `closed`.
export const WebNavEndBodySchema = Type.Object({
    trackId: Type.String({ format: 'uuid' }),
    sessionId: Type.String({ format: 'uuid' }),
    chatSessionId: Type.String({ format: 'uuid' }),
});
export const WebNavEndResponseSchema = Type.Object({
    closed: Type.Boolean(),
});
//# sourceMappingURL=web.js.map